import React, { useRef, useState } from 'react'
import { IonButton, IonButtons, IonDatetime, IonIcon, IonItem, IonLabel, IonPopover } from '@ionic/react'
import { calendarOutline } from 'ionicons/icons'
import './DateTimePicker.component.css'
import useDeviceMetrics from '../../../utilities/useDeviceMetrics'
import { IOptions, IPNNVOption } from '../../../common/common.types'
import { useDispatch, useSelector } from 'react-redux'
import {
  resetPNNVState,
  setNumberPadAndCalendarCurrentPNNVValue,
  setNumberPadAndCalendarOnPNNVChangeHandler,
  setNumberPadAndCalendarPNNVIsOpen,
  setNumberPadAndCalendarPNNVLabel,
  setNumberPadAndCalendarPNNVListHeader,
  setNumberPadAndCalendarPNNVNemsis,
  setNumberPadAndCalendarPNNVOptions,
} from '../../pnnv/store/pnnv.actions'
import PNNVBar from '../../pnnv/PNNVBar.component'
import { getPNNVStateSelector } from '../../pnnv/selectors'

export interface IDateTimePickerProps {
  dataTestID: string
  label?: string
  selectedDateValue: string | null
  action: (selectedDate: string | null) => void
  disabled: boolean
  hideClearBtn?: boolean
  className?: string
  showTimeSection?: boolean
  isShowIcon?: boolean
  pnnvOptions?: IOptions
  pnnvValue?: IPNNVOption | null | undefined
  pnnvAction?: (...args: any[]) => any
  pnnvLabel?: string
  pnnvListHeader?: string
  nemsis?: string
  useBothNormalAndPertinent?: boolean
}

const DateTimePicker: React.FC<IDateTimePickerProps> = (props: IDateTimePickerProps) => {
  const {
    dataTestID,
    label = '',
    selectedDateValue,
    disabled,
    action,
    hideClearBtn = false,
    className = '',
    showTimeSection = true,
    isShowIcon = true,
    pnnvOptions = [],
    pnnvValue = undefined,
    pnnvAction = () => () => void 0,
    pnnvLabel = '',
    pnnvListHeader = '',
    nemsis = 'nemsisCode',
    useBothNormalAndPertinent = false,
  } = props

  const { isTablet } = useDeviceMetrics()
  const labelStyle = !isTablet ? { fontSize: 'smaller' } : {}
  const [popoverState, setShowPopover] = useState(false)
  const [popupVisible, setPopupVisible] = useState<boolean>(false)
  const datetime = useRef<null | HTMLIonDatetimeElement>(null)
  const dispatch = useDispatch()
  const pnnvState = useSelector(getPNNVStateSelector)

  const setPNNVData = () => {
    dispatch(setNumberPadAndCalendarPNNVOptions(pnnvOptions))
    dispatch(setNumberPadAndCalendarCurrentPNNVValue(pnnvValue))
    dispatch(setNumberPadAndCalendarOnPNNVChangeHandler(pnnvAction))
    dispatch(setNumberPadAndCalendarPNNVLabel(pnnvLabel))
    dispatch(setNumberPadAndCalendarPNNVNemsis(nemsis))
    dispatch(setNumberPadAndCalendarPNNVListHeader(pnnvListHeader))
  }

  const handleClick = (e: any) => {
    if (pnnvValue?.name) {
      dispatch(setNumberPadAndCalendarPNNVIsOpen(true))
      setPopupVisible(true)
    } else {
      setPopupVisible(false)
    }
    setShowPopover(true)
    if (pnnvOptions.length) {
      setPNNVData()
    }
  }

  const clear = () => {
    datetime.current?.reset()
    action('')
    setTimeout(() => {
      resetPopoverState()
    }, 250)
  }

  const cancel = () => {
    dispatch(resetPNNVState())
    datetime.current?.cancel()
    setTimeout(() => {
      resetPopoverState()
    }, 250)
  }

  const save = () => {
    if (pnnvValue?.name) {
      pnnvAction(null)
      dispatch(setNumberPadAndCalendarCurrentPNNVValue(null))
    }
    datetime.current?.confirm()
    const date: any = datetime.current?.value
    if (date !== undefined) {
      const inputDate = formatDate(date)
      action(inputDate)
    }
    setTimeout(() => {
      resetPopoverState()
    }, 250)
  }
  const resetPopoverState = () => {
    setShowPopover(false)
    setPopupVisible(false)
    if (pnnvOptions.length > 0) {
      dispatch(resetPNNVState())
    }
  }

  function formatDate(inputDate: any) {
    if (!inputDate) return ''

    const date = new Date(inputDate)
    const month = ('0' + (date.getMonth() + 1)).slice(-2)
    const day = ('0' + date.getDate()).slice(-2)
    const year = date.getFullYear()
    const hours = ('0' + date.getHours()).slice(-2)
    const minutes = ('0' + date.getMinutes()).slice(-2)

    const formattedDateString = `${month}/${day}/${year} ${hours}:${minutes}`

    if (formattedDateString.includes('NaN')) {
      return ''
    }
    if (showTimeSection === false) {
      return `${month}/${day}/${year} `
    }
    return formattedDateString
  }

  function convertToISOString(inputDate: any) {
    if (!inputDate) return ''
    const [datePart, timePart] = inputDate.split(' ')
    const [month, day, year] = datePart.split('/')
    const [hours, minutes] = showTimeSection === false ? ['00', '00'] : timePart.split(':')

    const isoString =
      showTimeSection === false
        ? new Date(Date.UTC(year, month - 1, day)).toISOString().split('T')[0]
        : new Date(Date.UTC(year, month - 1, day, hours, minutes)).toISOString().replace('Z', '+05:30')
    if (isoString.includes('NaN')) {
      return ''
    }
    return isoString
  }

  return (
    <>
      <IonItem
        data-testid={dataTestID}
        disabled={disabled}
        lines='none'
        className={`date-picker-ion-item ${className}`}
        onClick={handleClick}>
        {isShowIcon && <IonIcon slot={'start'} size='large' color='medium' icon={calendarOutline} />}
        <div>
          {label && (
            <IonLabel
              position={selectedDateValue || pnnvValue?.name ? 'floating' : undefined}
              className='date-picker-ion-label'
              style={labelStyle}
              color='medium'>
              {label}
            </IonLabel>
          )}
          {!useBothNormalAndPertinent && (selectedDateValue || pnnvValue?.name) && (
            <div className='selected-date'>{pnnvValue?.name ? pnnvValue.name : selectedDateValue}</div>
          )}
          {useBothNormalAndPertinent && pnnvValue?.id === 8801029 && (
            <div className='selected-date'>
              <span className='mr-8'>~</span>
              <span>{selectedDateValue}</span>
            </div>
          )}
        </div>

        <IonPopover
          data-testid={dataTestID}
          showBackdrop={popoverState}
          isOpen={popoverState}
          onDidDismiss={resetPopoverState}>
          {pnnvOptions.length ? (
            <PNNVBar
              nonPNNVDataReset={() => {
                if (!useBothNormalAndPertinent && pnnvValue?.name) {
                  datetime.current?.reset()
                  action('')
                }
              }}
              actionOnDone={() => {
                dispatch(resetPNNVState())
                setTimeout(() => {
                  resetPopoverState()
                }, 250)
              }}
              showSingleSelectPopup={popupVisible}
            />
          ) : null}
          <IonDatetime
            className={pnnvState.numberPadAndCalendar.isOpen ? 'hidden-for-PNNV-option' : ''}
            presentation={showTimeSection ? 'date-time' : 'date'}
            ref={datetime}
            color='primary-alt'
            onClick={resetPopoverState}
            value={convertToISOString(selectedDateValue)}>
            <IonButtons slot='buttons'>
              <IonButton color='primary-alt' onClick={cancel}>
                Cancel
              </IonButton>
              {!hideClearBtn && (
                <IonButton color='primary-alt' onClick={clear}>
                  Clear
                </IonButton>
              )}
              <IonButton color='primary-alt' onClick={save}>
                Save
              </IonButton>
            </IonButtons>
          </IonDatetime>
        </IonPopover>
      </IonItem>
    </>
  )
}

export default DateTimePicker
